import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Form, Input, Select, Tooltip, Tag, Spin, message } from 'antd';
import { InfoCircleOutlined, CloseCircleFilled } from '@ant-design/icons';
import UserAvatar from '../../common/UserAvatar';
import {
    createDepartment,
    updateDepartment,
    getCompanyDepartment,
    getCompanyUsers,
} from '../api';

const DepartmentModal = ({ open, department, companyId, companyName, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const isEdit = !!department;

    // Load users + detail together so the form populates AFTER options exist.
    // (Ant Select can't resolve a value to a label if options are still empty
    //  when the value is set, and tag display gets stuck on the raw UUID.)
    useEffect(() => {
        if (!open || !companyId) return undefined;

        let cancelled = false;
        setLoading(true);

        const usersPromise = getCompanyUsers(companyId, { pageSize: 100 })
            .then((res) => res.data?.rows || [])
            .catch(() => []);

        const detailPromise = isEdit
            ? getCompanyDepartment(companyId, department.id).then((res) => res.data).catch(() => null)
            : Promise.resolve(null);

        Promise.all([usersPromise, detailPromise]).then(([companyUsers, detail]) => {
            if (cancelled) return;

            // Merge detail.assigned_users + calendar_user_info into the options list.
            // Users referenced from these fields may live outside the company-users query
            // (e.g. recruiting members whose home dept is in a different company), so we
            // ensure their full records are present so the Select can resolve labels.
            const byId = new Map(companyUsers.map((u) => [u.id, u]));
            if (detail?.assigned_users?.length) {
                detail.assigned_users.forEach((u) => {
                    if (!byId.has(u.id)) byId.set(u.id, u);
                });
            }
            if (detail?.calendar_user_info && !byId.has(detail.calendar_user_info.id)) {
                byId.set(detail.calendar_user_info.id, detail.calendar_user_info);
            }
            setUsers(Array.from(byId.values()));

            if (isEdit) {
                const d = detail || {};
                form.setFieldsValue({
                    name: d.name ?? department.name,
                    address: d.address1 ?? department.address1 ?? '',
                    city: d.city ?? department.city ?? '',
                    state: d.state ?? department.state ?? '',
                    zipcode: d.zipcode ?? department.zipcode ?? '',
                    connected_calendar_user: d.connected_calendar_user ?? null,
                    assigned_user_ids: (d.assigned_users || []).map((u) => u.id),
                });
            } else {
                form.resetFields();
            }
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
    }, [open, isEdit, department, companyId, form]);

    const usersById = useMemo(() => {
        const map = new Map();
        users.forEach((u) => map.set(u.id, u));
        return map;
    }, [users]);

    const userOptions = useMemo(() => users.map((u) => ({
        label: (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserAvatar name={u.name} userId={u.id} src={u.avatar_url} size={20} showTooltip={false} />
                <span style={{ flex: 1 }}>{u.name}</span>
                {u.has_connected_calendar === false && (
                    <Tag color="default" style={{ marginLeft: 'auto', fontSize: 11 }}>no calendar</Tag>
                )}
            </span>
        ),
        searchText: `${u.name || ''} ${u.email || ''}`,
        value: u.id,
    })), [users]);

    const renderUserTag = ({ value, onClose: onTagClose }) => {
        const u = usersById.get(value);
        return (
            <span
                className="ant-select-selection-item"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '2px 8px',
                    marginRight: 4,
                    background: '#f0f0f0',
                    borderRadius: 4,
                    fontSize: 13,
                }}
            >
                <UserAvatar name={u?.name} userId={value} src={u?.avatar_url} size={18} showTooltip={false} />
                <span>{u?.name || value}</span>
                <CloseCircleFilled
                    style={{ color: '#bfbfbf', cursor: 'pointer', fontSize: 12 }}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => { e.stopPropagation(); onTagClose(); }}
                />
            </span>
        );
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            if (isEdit) {
                await updateDepartment(companyId, department.id, {
                    name: values.name,
                    address1: values.address || '',
                    city: values.city || '',
                    state: values.state || '',
                    zipcode: values.zipcode || '',
                    connected_calendar_user: values.connected_calendar_user || null,
                    assigned_user_ids: values.assigned_user_ids || [],
                });
                message.success('Department updated');
            } else {
                await createDepartment(companyId, {
                    name: values.name,
                    address: values.address || '',
                    city: values.city || '',
                    state: values.state || '',
                    zipcode: values.zipcode || '',
                    company_name: companyName,
                });
                message.success('Department created');
            }
            onSuccess();
        } catch (err) {
            if (err.errorFields) return;
            message.error(err.response?.data?.message || 'Failed to save department');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={isEdit ? 'Edit Department' : 'Add Department'}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={submitting}
            destroyOnClose
            width={560}
        >
            <Spin spinning={loading && isEdit}>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label="Address">
                        <Input />
                    </Form.Item>
                    <Form.Item name="city" label="City" rules={[{ required: true, message: 'City is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="state" label="State" rules={[{ required: true, message: 'State is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="zipcode" label="Zipcode" rules={[{ required: true, message: 'Zipcode is required' }]}>
                        <Input />
                    </Form.Item>

                    {isEdit && (
                        <>
                            <Form.Item
                                name="connected_calendar_user"
                                label={(
                                    <span>
                                        Calendar user{' '}
                                        <Tooltip title="When an interview is scheduled for a candidate in this department, the event is added to this user's Cronofy calendar. Leave empty to fall back to the user who owns the bot that engaged the candidate.">
                                            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                        </Tooltip>
                                    </span>
                                )}
                            >
                                <Select
                                    allowClear
                                    placeholder="None — fall back to the bot owner"
                                    options={userOptions}
                                    optionFilterProp="searchText"
                                    showSearch
                                />
                            </Form.Item>

                            <Form.Item
                                name="assigned_user_ids"
                                label={(
                                    <span>
                                        Assigned users{' '}
                                        <Tooltip title="Users who get notified when a candidate lands in this department, and who are available for interview scheduling here. A user can be assigned to multiple departments. This is separate from a user's home department, which is set on the User edit screen.">
                                            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                        </Tooltip>
                                    </span>
                                )}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select users"
                                    options={userOptions}
                                    optionFilterProp="searchText"
                                    maxTagCount="responsive"
                                    tagRender={renderUserTag}
                                />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};

export default DepartmentModal;
